import React, {useEffect} from 'react';
import {StyleSheet} from 'react-native';
import {HStack, Text, VStack} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {
  FieldErrors,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import useToast from '../../../../../../hooks';
import {useSalesmanContext} from '../../../../../../context/salesman';
import {message as t} from '../../../../../../utils/messages';
import {StackParamList} from '../../../../../../navigators';
import InternalTemplate from '../../../../../../components/templates/internal';
import {ReadonlyInput} from '../../../../../../components/inputs';
import {TextInput} from '../../../../../../components/inputs/text';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../../../../../components/buttons';
import {formatMoney, formatPercent} from '../../../../../../utils/format';

interface IOrderItemToAdd {
  count: string | null;
}

type ScreenProps = NativeStackScreenProps<StackParamList, 'InvoiceItemForm'>;

const InvoiceItemForm = ({navigation}: ScreenProps) => {
  const {invoiceItemEditing, invoiceCandidate, setInvoiceCandidate} =
    useSalesmanContext();
  const {showInputsError} = useToast();

  const {
    handleSubmit,
    control,
    reset,
    formState: {errors},
    watch,
    trigger,
  } = useForm({
    defaultValues: {
      count:
        invoiceItemEditing?.count != null
          ? `${invoiceItemEditing?.count}`
          : null,
    } as IOrderItemToAdd,
  });

  const count = watch('count');

  useEffect(() => {
    trigger('count');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  const handleGoBack = () => {
    navigation.goBack();
  };

  const handleOnContinueSubmit: SubmitHandler<IOrderItemToAdd> = async (
    data: IOrderItemToAdd,
  ) => {
    if (invoiceItemEditing) {
      try {
        if (data.count !== null) {
          const countValue = parseInt(data.count, 10);
          if (countValue.toString() === data.count) {
            invoiceItemEditing.count = countValue;

            if (invoiceCandidate) {
              setInvoiceCandidate({...invoiceCandidate});
            }
            navigation.navigate('InvoiceAdjustableItems');
            return;
          }
        }
      } catch (e) {}
    }
    showInputsError();
  };

  const onInvalid: SubmitErrorHandler<IOrderItemToAdd> = async (
    fieldErrors: FieldErrors<IOrderItemToAdd>,
  ) => {
    if (fieldErrors) {
      showInputsError();
    }
  };

  const resetForm = () => {
    reset();
  };

  const getPrice = () => {
    return invoiceItemEditing ? invoiceItemEditing?.price : 0;
  };

  const showPrice = (showSymbol = false) => {
    return invoiceItemEditing
      ? formatMoney(getPrice(), showSymbol ? getMoneySymbol() : '')
      : '';
  };

  const getIva = (showSymbol = false) => {
    return formatPercent(invoiceItemEditing!.ivaPercent, showSymbol);
  };

  const getSubTotal = () => {
    return invoiceItemEditing ? invoiceItemEditing?.price * getCount() : 0;
  };

  const showSubTotal = (showSymbol = false) => {
    return invoiceItemEditing
      ? formatMoney(getSubTotal(), showSymbol ? getMoneySymbol() : '')
      : '';
  };

  const getCalculatedIva = () => {
    if (invoiceItemEditing) {
      return (getSubTotal() * invoiceItemEditing!.ivaPercent) / 100;
    }
    return 0;
  };

  const showCalculatedIva = (showSymbol = false) => {
    if (invoiceItemEditing) {
      return formatMoney(
        getCalculatedIva(),
        showSymbol ? '' : getMoneySymbol(),
      );
    }
    return 0;
  };

  const getTotal = () => {
    return getSubTotal() + getCalculatedIva();
  };

  const showTotal = (showSymbol = false) => {
    return formatMoney(getTotal(), showSymbol ? getMoneySymbol() : '');
  };

  const getMoneySymbol = () => {
    return invoiceCandidate?.currency === 'BS' ? 'Bs.' : '$';
  };

  const getCount = () => {
    try {
      if (errors.count == null) {
        const value = Number(count);
        return value;
      }
    } catch {}

    return 0;
  };

  return (
    <InternalTemplate
      title={'salesman-invoice-item-form-title'}
      contentPaddingX="3"
      backButton={{
        onPress: () => handleGoBack(),
        label: 'go-back',
        usage: 'external',
      }}>
      <VStack marginX={5}>
        <ReadonlyInput
          label={'salesman-invoice-item-form-product-title'}
          value={` ${invoiceItemEditing?.product.title}`}
          icon={undefined}
          width={'100%'}
          backgroundColor="#EFEFEF"
        />
        <ReadonlyInput
          label={'salesman-invoice-item-form-unit-title'}
          value={`${invoiceItemEditing?.unit.title}`}
          icon={undefined}
          width={'100%'}
          backgroundColor="#EFEFEF"
        />
        <HStack justifyContent={'space-between'}>
          <TextInput
            type="number"
            label={'Cantidad'}
            error={errors.count}
            name={'count'}
            control={control}
            width={'45%'}
            rules={{
              required: 'add-products-products-required',
              pattern: {
                value: new RegExp('^[0-9]+$'),
                message: 'add-products-count-products-only-numbers',
              },
            }}
          />

          <ReadonlyInput
            label={`${invoiceItemEditing?.unit.subUnit.title}`}
            value={`${invoiceItemEditing?.unit.subUnit.size}`}
            icon={undefined}
            width={'45%'}
            backgroundColor="#EFEFEF"
          />
        </HStack>

        <HStack justifyContent={'space-between'}>
          <ReadonlyInput
            label={`Precio ${getMoneySymbol()}`}
            value={showPrice(false)}
            icon={undefined}
            width={'45%'}
            backgroundColor="#EFEFEF"
          />

          <ReadonlyInput
            label={'IVA %'}
            value={getIva()}
            icon={undefined}
            width={'45%'}
            backgroundColor="#EFEFEF"
          />
        </HStack>

        <VStack style={styles.summaryBox}>
          <HStack justifyContent={'space-between'} style={styles.summaryItem}>
            <Text style={styles.summaryItemLabel}>
              {t('salesman-order-add-summary-subtotal')}
            </Text>
            <Text style={styles.summaryItemValue}>{showSubTotal(true)}</Text>
          </HStack>
          <HStack justifyContent={'space-between'} style={styles.summaryItem}>
            <Text style={styles.summaryItemLabel}>
              {t('salesman-order-add-summary-iva')} - {getIva(true)}
            </Text>
            <Text style={styles.summaryItemValue}>{showCalculatedIva()}</Text>
          </HStack>
          <HStack justifyContent={'space-between'} style={styles.summaryItem}>
            <Text style={styles.summaryItemLabel}>
              {t('salesman-order-add-summary-total')}
            </Text>
            <Text style={styles.summaryItemValue}>{showTotal(true)}</Text>
          </HStack>

          <VStack paddingX={5} marginY={5}>
            <PrimaryButton
              label="salesman-invoice-item-form--save-button"
              onPress={handleSubmit(handleOnContinueSubmit, onInvalid)}
            />
          </VStack>
        </VStack>
        <VStack style={styles.resetBox}>
          <SecondaryButton
            label="salesman-invoice-item-form-reset-button"
            onPress={resetForm}
          />
        </VStack>
      </VStack>
    </InternalTemplate>
  );
};

export default InvoiceItemForm;

const styles = StyleSheet.create({
  summaryItem: {
    borderBottomWidth: 1,
    borderColor: '#0000001F',
    marginBottom: 7,
    marginTop: 5,
  },
  summaryItemLabel: {
    color: '#00000099',
  },
  summaryItemLabelTotal: {
    color: '#000000DE',
  },
  summaryItemValue: {
    color: '#000000DE',
  },
  summaryBox: {
    backgroundColor: '#F1F3F4',
    padding: 22,
    borderRadius: 10,
    marginTop: 30,
  },
  resetBox: {
    marginTop: 20,
    marginBottom: 150,
  },
  companyIcon: {
    marginLeft: 10,
  },
});
